import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Lightbulb, Puzzle, Rocket, Shield, Users, Zap } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function CustomSolutions() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: Lightbulb,
      title: "Tailored to Your Needs",
      description: "Solutions designed specifically for your unique business challenges"
    },
    {
      icon: Puzzle,
      title: "Seamless Integration",
      description: "Works with your existing systems and workflows"
    },
    {
      icon: Rocket,
      title: "Scalable Architecture",
      description: "Grows with your business without needing a rebuild"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Built with security and compliance in mind from day one"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "Ongoing support and maintenance to keep everything running smoothly"
    },
    {
      icon: Zap,
      title: "Rapid Development",
      description: "Modern development practices that get you to market faster"
    },
  ];

  const examples = [
    {
      title: "Custom CRM System",
      description: "A real estate company needed a CRM that matched their unique sales process. We built a custom solution that increased their close rate by 40%."
    },
    {
      title: "Inventory Management Platform",
      description: "A growing e-commerce business outgrew off-the-shelf tools. Our custom platform reduced fulfillment errors by 85%."
    },
    {
      title: "Client Portal",
      description: "A consulting firm wanted to give clients 24/7 access to project status. We built a branded portal that improved client satisfaction scores by 60%."
    },
    {
      title: "Booking & Scheduling System",
      description: "A service business with complex scheduling needs couldn't find software that worked. Our custom solution eliminated double-bookings and saved 15 hours per week."
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-primary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gradient">Custom Solutions</span> for Unique Challenges
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              When off-the-shelf software doesn't cut it, we build exactly what you need.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-secondary to-primary text-white"
              >
                Discuss Your Project
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Go Custom?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-secondary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <feature.icon className="w-12 h-12 text-secondary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Example Projects */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Real Solutions, Real Results
            </h2>
            
            <div className="space-y-6">
              {examples.map((example, index) => (
                <motion.div
                  key={example.title}
                  className="p-6 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-2xl font-semibold mb-3">{example.title}</h3>
                  <p className="text-muted-foreground text-lg">{example.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* When to Go Custom */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Is Custom Right for You?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "You've outgrown off-the-shelf software",
                "Your process is unique to your industry",
                "You need specific integrations that don't exist",
                "You want a competitive advantage through technology",
                "You're tired of paying for features you don't use",
                "You need full control over your data and systems",
              ].map((reason, index) => (
                <motion.div
                  key={reason}
                  className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-lg">{reason}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              How We Work
            </h2>
            
            <div className="space-y-6">
              {[
                { step: "1", title: "Discovery Workshop", description: "Deep dive into your business, challenges, and goals" },
                { step: "2", title: "Solution Design", description: "Create detailed specifications and prototypes" },
                { step: "3", title: "Iterative Development", description: "Build in sprints with regular check-ins and demos" },
                { step: "4", title: "Testing & Refinement", description: "Rigorous testing to ensure everything works perfectly" },
                { step: "5", title: "Launch & Training", description: "Deploy to production and train your team" },
                { step: "6", title: "Ongoing Support", description: "Maintenance, updates, and enhancements as needed" },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  className="flex gap-6 items-start"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Let's Build Your Solution
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Tell us about your challenge, and we'll show you how custom software can solve it.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-secondary to-primary text-white"
            >
              Start the Conversation
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
